import { useEffect, useRef, useState } from 'react';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, SearchIcon } from '@/components/icons';
export type SettingsTab =
  | 'profile'
  | 'appearance'
  | 'voice'
  | 'notifications'
  | 'shortcuts'
  | 'privacy'
  | 'moderation';

export const TAB_META: Array<{
  key: SettingsTab;
  /** Translation key under `prof.settings.tab.*`. */
  labelKey: string;
  /** Optional badge next to the label, e.g. "новое". */
  badge?: string;
}> = [
  { key: 'profile', labelKey: 'prof.settings.tab.profile' },
  { key: 'appearance', labelKey: 'prof.settings.tab.appearance' },
  { key: 'voice', labelKey: 'prof.settings.tab.voice' },
  { key: 'notifications', labelKey: 'prof.settings.tab.notifications' },
  { key: 'shortcuts', labelKey: 'prof.settings.tab.shortcuts' },
  { key: 'privacy', labelKey: 'prof.settings.tab.privacy' },
];

/** Appended to the nav only for platform moderators (see useReportAccess). */
export const MODERATION_META = {
  key: 'moderation' as SettingsTab,
  labelKey: 'prof.settings.tab.moderation',
};

/** Settings sections this viewer gets (moderation only for platform moderators). */
export function settingsNavItems(opts: { platformAdmin: boolean; profile?: boolean }): typeof TAB_META {
  // «Профиль» goes when the shell owns everything in it (lib/platformIdentity.ts, lib/shellPresence.ts).
  const sections = opts.profile === false ? TAB_META.filter(({ key }) => key !== 'profile') : TAB_META;
  return opts.platformAdmin ? [...sections, MODERATION_META] : sections;
}

type NavigationProps = {
  tab: SettingsTab;
  onPick: (tab: SettingsTab) => void;
  items: typeof TAB_META;
};

export function SettingsSidebar({ tab, onPick, items }: NavigationProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const visible = items.filter(({ labelKey }) => t(labelKey).toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const personal = visible.filter(({ key }) => ['profile', 'appearance', 'voice', 'notifications'].includes(key));
  const additional = visible.filter(({ key }) => !['profile', 'appearance', 'voice', 'notifications'].includes(key));
  const rows = (group: typeof items) => group.map(({ key, labelKey }) => (
    <button key={key} type="button" aria-current={tab === key ? 'page' : undefined}
      onClick={() => onPick(key)} className="app-settings-nav-row">
      <span>{t(labelKey)}</span>
    </button>
  ));
  return (
    <nav aria-label={t('prof.settings.title')} className="app-settings-sidebar">
      <label className="app-settings-search">
        <SearchIcon className="size-4 shrink-0" />
        <input value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder={t('prof.settings.searchSettings')} aria-label={t('prof.settings.searchSettings')} />
      </label>
      {personal.length > 0 && <div className="app-settings-nav-section">
        <div className="app-settings-nav-caption">{t('prof.settings.personal')}</div>
        {rows(personal)}
      </div>}
      {additional.length > 0 && <div className="app-settings-nav-section">
        <div className="app-settings-nav-caption">{t('prof.settings.additional')}</div>
        {rows(additional)}
      </div>}
      {visible.length === 0 && <p className="app-settings-nav-empty">{t('prof.settings.noSettingsFound')}</p>}
    </nav>
  );
}

export function MobileSettingsIndex({ tab, onPick, items, userId, name, avatarUrl, restoreFocus, showIdentity = true }: NavigationProps & {
  userId: string;
  name: string;
  avatarUrl: string;
  restoreFocus: boolean;
  /** Name and avatar card for «Профиль». Off in the platform frame: the
   *  shell shows who is signed in; «Профиль» stays an ordinary row. */
  showIdentity?: boolean;
}) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (restoreFocus) navRef.current?.querySelector<HTMLButtonElement>(`[data-settings-section="${tab}"]`)?.focus();
  }, [restoreFocus, tab]);
  return (
    <div ref={navRef} className="app-settings-index">
      {showIdentity && items.some(({ key }) => key === 'profile') && (
        <button type="button" data-settings-section="profile" onClick={() => onPick('profile')}
          className="app-settings-identity app-settings-nav-row">
          <IdentityAvatar id={userId} name={name} src={avatarUrl} className="app-settings-avatar" />
          <span className="min-w-0 flex-1">
            <span className="block break-words text-lg font-semibold">{name}</span>
            <span className="block text-sm text-ink-muted">{t('prof.settings.tab.profile')}</span>
          </span>
          <ChevronDownIcon className="size-4 shrink-0 -rotate-90 text-ink-muted" />
        </button>
      )}
      <nav aria-label={t('prof.settings.title')} className="app-settings-nav-group">
        {items.filter(({ key }) => !showIdentity || key !== 'profile').map(({ key, labelKey }) => (
          <button key={key} type="button" data-settings-section={key} onClick={() => onPick(key)}
            className="app-settings-nav-row">
            <span className="min-w-0 flex-1">{t(labelKey)}</span>
            <ChevronDownIcon className="size-4 shrink-0 -rotate-90 text-ink-muted" />
          </button>
        ))}
      </nav>
    </div>
  );
}
